import { formatDateYMD } from "../../../../shared/utils/date";
import { csvEscape } from "../../../company/utils/csvAndDateUtils";
import { downloadCsvFile } from "../../UserLevelDataTab/utils";
import type { ActivityLineChartDataPoint } from "../../utils/chartDataUtils";
import type { ActivityTrendDataPoint } from "../../utils/trendChartDataUtils";
import type { CountryMetricRow, ExperienceFeedbackRating } from "../types";
import { type ExperienceUsageData, MIN_DISPLAY_VALUE } from "./usageUtils";

const MASKED_VALUE = `<${MIN_DISPLAY_VALUE}`;

const UNREPORTED_VALUE = "";

function maskValue(
  value: number | null,
  isUserLevelDataEnabled: boolean,
): string {
  if (value === null) return UNREPORTED_VALUE;
  if (!isUserLevelDataEnabled && value < MIN_DISPLAY_VALUE) return MASKED_VALUE;

  return String(value);
}

function maskHoursValue(
  value: number | null,
  isUserLevelDataEnabled: boolean,
): string {
  if (value === null) return UNREPORTED_VALUE;
  if (!isUserLevelDataEnabled && value < MIN_DISPLAY_VALUE) return MASKED_VALUE;

  return value.toFixed(1);
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Inserts the selected date range into the filename before its extension,
 * e.g. "activity.csv" -> "activity_2024-01-01_to_2024-06-30.csv".
 */
function withDateRangeFilename(filename: string, dateRange: DateRange): string {
  const suffix = `_${formatDateYMD(dateRange.startDate)}_to_${formatDateYMD(dateRange.endDate)}`;
  const dotIndex = filename.lastIndexOf(".");

  return dotIndex === -1
    ? `${filename}${suffix}`
    : `${filename.slice(0, dotIndex)}${suffix}${filename.slice(dotIndex)}`;
}

function buildDateRangeRows(
  dateRange: DateRange,
  labels: { startDate: string; endDate: string },
): string[] {
  return [
    `${csvEscape(labels.startDate)},${csvEscape(formatDateYMD(dateRange.startDate))}`,
    `${csvEscape(labels.endDate)},${csvEscape(formatDateYMD(dateRange.endDate))}`,
    "",
  ];
}

export function exportActivityChartCsv(
  data: Array<ActivityLineChartDataPoint | ActivityTrendDataPoint>,
  isUserLevelDataEnabled: boolean,
  columnHeaders: {
    period: string;
    periodStart: string;
    periodEnd: string;
    hoursInVr: string;
    userSessions: string;
    uniqueActiveUsers: string;
  },
  filename: string,
  dateRange: DateRange,
  dateRangeLabels: { startDate: string; endDate: string },
  licenseFilter: { label: string; names: string[] },
): void {
  const metadataRows = buildDateRangeRows(dateRange, dateRangeLabels);

  if (licenseFilter.names.length > 0) {
    metadataRows.push(
      `${csvEscape(licenseFilter.label)},${licenseFilter.names.map(csvEscape).join(",")}`,
      "",
    );
  }

  const headers = [
    columnHeaders.period,
    columnHeaders.periodStart,
    columnHeaders.periodEnd,
    columnHeaders.hoursInVr,
    columnHeaders.userSessions,
    columnHeaders.uniqueActiveUsers,
  ];

  const headerRow = headers.map(csvEscape).join(",");
  const rows = data.map((d) =>
    [
      csvEscape(d.tickLabel.replaceAll("\n", " ")),
      csvEscape(formatDateYMD(d.periodStart)),
      csvEscape(formatDateYMD(d.periodEnd)),
      csvEscape(maskHoursValue(d.hoursInVr, isUserLevelDataEnabled)),
      csvEscape(maskValue(d.userSessions, isUserLevelDataEnabled)),
      csvEscape(maskValue(d.uniqueActiveUsers, isUserLevelDataEnabled)),
    ].join(","),
  );

  downloadCsvFile(
    [...metadataRows, headerRow, ...rows].join("\r\n"),
    withDateRangeFilename(filename, dateRange),
  );
}

export function exportUsagePerExperienceCsv(
  data: ExperienceUsageData[],
  isUserLevelDataEnabled: boolean,
  columnHeaders: {
    experienceName: string;
    hoursInVr: string;
    userSessions: string;
    uniqueActiveUsers: string;
    certifications: string;
  },
  filename: string,
  dateRange: DateRange,
  dateRangeLabels: { startDate: string; endDate: string },
  showCertifications = false,
): void {
  const metadataRows = buildDateRangeRows(dateRange, dateRangeLabels);

  const headers = [
    columnHeaders.experienceName,
    columnHeaders.hoursInVr,
    columnHeaders.userSessions,
    columnHeaders.uniqueActiveUsers,
    ...(showCertifications ? [columnHeaders.certifications] : []),
  ];

  const headerRow = headers.map(csvEscape).join(",");
  const rows = data.map((d) =>
    [
      csvEscape(d.name),
      csvEscape(maskHoursValue(d.hoursInVr, isUserLevelDataEnabled)),
      csvEscape(maskValue(d.userSessions, isUserLevelDataEnabled)),
      csvEscape(maskValue(d.uniqueActiveUsers, isUserLevelDataEnabled)),
      ...(showCertifications
        ? [csvEscape(maskValue(d.certifications, isUserLevelDataEnabled))]
        : []),
    ].join(","),
  );

  downloadCsvFile(
    [...metadataRows, headerRow, ...rows].join("\r\n"),
    withDateRangeFilename(filename, dateRange),
  );
}

export function exportCountryUsageCsv(
  rows: CountryMetricRow[],
  isUserLevelDataEnabled: boolean,
  columnHeaders: {
    country: string;
    value: string;
    percentage: string;
  },
  filename: string,
  dateRange: DateRange,
  dateRangeLabels: { startDate: string; endDate: string },
): void {
  const metadataRows = buildDateRangeRows(dateRange, dateRangeLabels);

  const headers = [
    columnHeaders.country,
    columnHeaders.value,
    columnHeaders.percentage,
  ];

  const headerRow = headers.map(csvEscape).join(",");

  const csvRows: string[] = [];
  for (const row of rows) {
    if (row.isOther && row.otherCountries?.length) {
      for (const sub of row.otherCountries) {
        csvRows.push(
          [
            csvEscape(sub.name),
            csvEscape(maskValue(sub.metricValue, isUserLevelDataEnabled)),
            "",
          ].join(","),
        );
      }
    } else {
      const pct = row.percentage >= 0 ? `${row.percentage.toFixed(1)}%` : "N/A";
      csvRows.push(
        [
          csvEscape(row.name),
          csvEscape(maskValue(row.metricValue, isUserLevelDataEnabled)),
          csvEscape(pct),
        ].join(","),
      );
    }
  }

  downloadCsvFile(
    [...metadataRows, headerRow, ...csvRows].join("\r\n"),
    withDateRangeFilename(filename, dateRange),
  );
}

export function exportFeedbackRatingsCsv(
  data: ExperienceFeedbackRating[],
  columnHeaders: {
    experienceName: string;
    practiceRating: string;
    assessmentRating: string;
    practiceFeedbackCount: string;
    assessmentFeedbackCount: string;
  },
  filename: string,
  dateRange: DateRange,
  dateRangeLabels: { startDate: string; endDate: string },
): void {
  const metadataRows = buildDateRangeRows(dateRange, dateRangeLabels);

  const headers = [
    columnHeaders.experienceName,
    columnHeaders.practiceRating,
    columnHeaders.assessmentRating,
    columnHeaders.practiceFeedbackCount,
    columnHeaders.assessmentFeedbackCount,
  ];

  const headerRow = headers.map(csvEscape).join(",");
  const rows = data.map((d) =>
    [
      csvEscape(d.experienceName),
      d.practiceRating === null ? "N/A" : d.practiceRating.toFixed(1),
      d.assessmentRating === null ? "N/A" : d.assessmentRating.toFixed(1),
      String(d.practiceFeedbackCount),
      String(d.assessmentFeedbackCount),
    ].join(","),
  );

  downloadCsvFile(
    [...metadataRows, headerRow, ...rows].join("\r\n"),
    withDateRangeFilename(filename, dateRange),
  );
}
